import { gql } from '@apollo/client'

export const GET_CUSTOMERS_GROUPS = gql`
  query FindManyCustomerGroup {
    findManyCustomerGroup {
      name
      id
    }
  }
`

export const GET_PRICELISTS = gql`
  query FindManyPriceList {
    findManyPriceList {
      name
      id
      default
    }
  }
`

export const GET_B2B_CUSTOMERS = gql`
  query GET_B2B_CUSTOMERS(
    $searchTerm: String
    $take: Int
    $skip: Int
    $orderBy: [CustomerOrderByWithRelationInput!]
    $filters: [CustomerWhereInput] = []
    $language: Language
  ) {
    findManyCustomerCount(
      where: {
        OR: [
          { name: { mode: insensitive, contains: $searchTerm } }
          { surname: { mode: insensitive, contains: $searchTerm } }
          { b2b_company_name: { mode: insensitive, contains: $searchTerm } }
          { account_code: { mode: insensitive, contains: $searchTerm } }
        ]
        is_b2b_user: { equals: true }
        role: { not: { in: [manager, agent, admin] } }
        deletedAt: { equals: null }
        AND: $filters
      }
    )

    findManyCustomer(
      orderBy: $orderBy
      where: {
        OR: [
          { name: { mode: insensitive, contains: $searchTerm } }
          { surname: { mode: insensitive, contains: $searchTerm } }
          { b2b_company_name: { mode: insensitive, contains: $searchTerm } }
          { account_code: { mode: insensitive, contains: $searchTerm } }
        ]
        is_b2b_user: { equals: true }
        role: { not: { in: [manager, agent, admin] } }
        deletedAt: { equals: null }
        AND: $filters
      }
      take: $take
      skip: $skip
    ) {
      id
      createdAt
      name
      surname
      email
      default_phone_number
      language
      buying_as_company
      b2b_company_address {
        country
      }
      is_b2b_user
      b2b_company_name
      b2b_customer_discount
      b2b_company_country
      account_code
      customer_group {
        id
        name
      }
      orders {
        id
      }
      price_list {
        id
        name
      }
      fabric_palettes {
        id
        name
      }
      tags {
        id
        tag_profiles {
          name
          language
        }
      }
      additional_components {
        id
        additionalComponent {
          code
          additional_component_profiles(
            where: { language: { equals: $language } }
          ) {
            name
            language
          }
        }
      }
    }
  }
`

export const CORE_B2B_CUSTOMER_DATA = gql`
  fragment CoreB2BCustomerData on Customer {
    id
    createdAt
    updatedAt

    name
    surname
    email
    default_phone_number
    language

    is_b2b_user
    b2b_customer_discount
    b2b_company_name
    b2b_company_code
    b2b_company_vat_code
    b2b_company_website
    fabric_palettes {
      id
    }
    files {
      id
      name
      storage_key
      original_name
      mime_type
      size_bytes
      display_order
    }
    b2b_company_address {
      id
      phone_number
      country
      city
      postal_code
      state_region
      address_1
      address_2
    }

    b2b_company_country

    customer_accounts {
      id
      createdAt
      updatedAt
      customerId
      email
      name
      notes
      status
    }

    customer_group {
      id
      name
    }
    price_listId
    account_code
    addresses {
      id
      country
      city
      postal_code
      state_region
      address_1
      address_2
      roles
      description
    }
    tags {
      id
    }
    managers {
      id
      managerId
      manager {
        name
        role
      }
    }
    is_configurator_enabled
    is_claims_enabled
    additional_components {
      id
      additionalComponent {
        id
        additionalComponentGroupId
        additional_component_profiles(
          where: { language: { equals: $language } }
        ) {
          name
          language
        }
      }
    }
  }
`

export const CREATE_B2B_CUSTOMER = gql`
  ${CORE_B2B_CUSTOMER_DATA}
  mutation CREATE_B2B_CUSTOMER(
    $name: String!
    $surname: String
    $email: String!
    $default_phone_number: String
    $is_b2b_user: Boolean!
    $b2b_customer_discount: Int
    $b2b_company_name: String
    $b2b_company_code: String
    $b2b_company_vat_code: String
    $b2b_company_website: String
    $country: String!
    $city: String!
    $postal_code: String!
    $state_region: String
    $address_1: String!
    $address_2: String
    $customer_groupId: Int
    $price_listId: Int
    $account_code: String!
    $phone_number: String
    $tags: [TagWhereUniqueInput]
    $additional_components: [Int]
    $managers: [Int]
    $language: Language
    $fabric_palettes: [Int]
    $files: [CustomerFileInput]
    $is_configurator_enabled: Boolean
    $is_claims_enabled: Boolean
  ) {
    createB2Bcustomer(
      name: $name
      surname: $surname
      email: $email
      default_phone_number: $default_phone_number
      is_b2b_user: $is_b2b_user
      b2b_customer_discount: $b2b_customer_discount
      b2b_company_name: $b2b_company_name
      b2b_company_code: $b2b_company_code
      b2b_company_vat_code: $b2b_company_vat_code
      b2b_company_website: $b2b_company_website
      phone_number: $phone_number
      country: $country
      city: $city
      postal_code: $postal_code
      state_region: $state_region
      address_1: $address_1
      address_2: $address_2
      customer_groupId: $customer_groupId
      price_listId: $price_listId
      account_code: $account_code
      tags: $tags
      additional_components: $additional_components
      managers: $managers
      fabric_palettes: $fabric_palettes
      files: $files
      is_configurator_enabled: $is_configurator_enabled
      is_claims_enabled: $is_claims_enabled
    ) {
      ...CoreB2BCustomerData
    }
  }
`

export const UPDATE_B2B_CUSTOMER = gql`
  ${CORE_B2B_CUSTOMER_DATA}
  mutation UPDATE_B2B_CUSTOMER(
    $id: Int!
    $name: String!
    $surname: String
    $email: String!
    $default_phone_number: String
    $is_b2b_user: Boolean!
    $b2b_customer_discount: Int
    $b2b_company_name: String
    $b2b_company_code: String
    $b2b_company_vat_code: String
    $b2b_company_website: String
    $phone_number: String!
    $country: String!
    $city: String!
    $postal_code: String!
    $state_region: String
    $address_1: String!
    $address_2: String
    $customer_groupId: Int
    $price_listId: Int
    $account_code: String!
    $tags: [TagWhereUniqueInput]
    $additional_components: [Int]
    $language: Language
    $managers: [Int]
    $fabric_palettes: [Int]
    $files: [CustomerFileInput]
    $is_configurator_enabled: Boolean
    $is_claims_enabled: Boolean
  ) {
    updateB2Bcustomer(
      where: { id: $id }
      name: $name
      surname: $surname
      email: $email
      default_phone_number: $default_phone_number
      is_b2b_user: $is_b2b_user
      b2b_customer_discount: $b2b_customer_discount
      b2b_company_name: $b2b_company_name
      b2b_company_code: $b2b_company_code
      b2b_company_vat_code: $b2b_company_vat_code
      b2b_company_website: $b2b_company_website
      phone_number: $phone_number
      country: $country
      city: $city
      postal_code: $postal_code
      state_region: $state_region
      address_1: $address_1
      address_2: $address_2
      customer_groupId: $customer_groupId
      price_listId: $price_listId
      account_code: $account_code
      tags: $tags
      additional_components: $additional_components
      managers: $managers
      fabric_palettes: $fabric_palettes
      files: $files
      is_configurator_enabled: $is_configurator_enabled
      is_claims_enabled: $is_claims_enabled
    ) {
      ...CoreB2BCustomerData
    }
  }
`

export const GET_B2B_CUSTOMER = gql`
  ${CORE_B2B_CUSTOMER_DATA}
  query GET_B2B_CUSTOMER($id: Int!, $language: Language) {
    findUniqueCustomer(where: { id: $id }) {
      ...CoreB2BCustomerData
    }
  }
`

export const IMPERSONATE_B2B_CUSTOMER = gql`
  mutation IMPERSONATE_B2B_CUSTOMER(
    $impersonateB2BCustomerId: Int!
    $managerId: Int!
  ) {
    impersonateB2BCustomer(id: $impersonateB2BCustomerId, managerId: $managerId)
  }
`

export const DELETE_MANY_CUSTOMERS = gql`
  mutation DELETE_MANY_B2B_CUSTOMERS($ids: [Int!]!) {
    deleteManyCustomer(where: { id: { in: $ids } }) {
      count
    }
  }
`

export const GET_UNIQUE_COUNTRIES = gql`
  query GET_UNIQUE_COUNTRIES {
    getUniqueCountries
  }
`

export const BULK_UPDATE_B2B_CUSTOMERS = gql`
  mutation BULK_UPDATE_B2B_CUSTOMERS(
    $customerIds: [Int!]
    $filters: [CustomerWhereInput]
    $searchTerm: String
    $is_configurator_enabled: Boolean
    $is_claims_enabled: Boolean
    $palettes: [Int!]
    $palettesAction: String
  ) {
    bulkUpdateB2BCustomers(
      customerIds: $customerIds
      filters: $filters
      searchTerm: $searchTerm
      is_configurator_enabled: $is_configurator_enabled
      is_claims_enabled: $is_claims_enabled
      palettes: $palettes
      palettesAction: $palettesAction
    ) {
      count
    }
  }
`

export const DELETE_SINGLE_B2B_CUSTOMER = gql`
  mutation DELETE_SINGLE_B2B_CUSTOMER($id: Int!) {
    deleteSingleB2BCustomer(id: $id)
  }
`

export const GET_DELETED_B2B_CUSTOMERS = gql`
  query GET_DELETED_B2B_CUSTOMERS(
    $searchTerm: String
    $take: Int
    $skip: Int
    $orderBy: [CustomerOrderByWithRelationInput!]
    $language: Language
  ) {
    findManyCustomerCount(
      where: {
        OR: [
          { name: { mode: insensitive, contains: $searchTerm } }
          { surname: { mode: insensitive, contains: $searchTerm } }
          { b2b_company_name: { mode: insensitive, contains: $searchTerm } }
          { account_code: { mode: insensitive, contains: $searchTerm } }
        ]
        deletedAt: { not: null }
        role: { not: { in: [manager, agent, admin] } }
      }
    )

    findManyCustomer(
      orderBy: $orderBy
      where: {
        OR: [
          { name: { mode: insensitive, contains: $searchTerm } }
          { surname: { mode: insensitive, contains: $searchTerm } }
          { b2b_company_name: { mode: insensitive, contains: $searchTerm } }
          { account_code: { mode: insensitive, contains: $searchTerm } }
        ]
        deletedAt: { not: null }
        role: { not: { in: [manager, agent, admin] } }
      }
      take: $take
      skip: $skip
    ) {
      id
      createdAt
      deletedAt
      name
      surname
      email
      default_phone_number
      language
      buying_as_company
      b2b_company_address {
        country
      }
      is_b2b_user
      b2b_company_name
      b2b_customer_discount
      b2b_company_country
      account_code
      customer_group {
        id
        name
      }
      orders {
        id
      }
      price_list {
        id
        name
      }
      fabric_palettes {
        id
        name
      }
      tags {
        id
        tag_profiles {
          name
          language
        }
      }
      additional_components {
        id
        additionalComponent {
          code
          additional_component_profiles(
            where: { language: { equals: $language } }
          ) {
            name
            language
          }
        }
      }
    }
  }
`

export const RESTORE_B2B_CUSTOMER = gql`
  mutation RESTORE_B2B_CUSTOMER($id: Int!, $restoreAsB2B: Boolean) {
    restoreCustomer(id: $id, restoreAsB2B: $restoreAsB2B)
  }
`

export const UPLOAD_CUSTOMER_FILE = gql`
  mutation uploadCustomerFile($file: Upload!) {
    uploadCustomerFile(file: $file) {
      storage_key
      original_name
      mime_type
      size_bytes
    }
  }
`

export const BULK_ADD_CUSTOMER_FILES = gql`
  mutation bulkAddCustomerFiles(
    $customerIds: [Int!]!
    $files: [CustomerFileInput!]!
  ) {
    bulkAddCustomerFiles(customerIds: $customerIds, files: $files) {
      customerCount
      fileCount
      skippedCount
    }
  }
`
